/**
 * DIABEA Landing Page JavaScript
 * Shopify Online Store 2.0 Compatible
 *
 * Current behavior (CRO flow):
 * - Adds products to cart using Shopify AJAX endpoint (/cart/add.js)
 * - After adding MAIN product: shows optional upsell modal, then redirects to CART
 * - After adding UPSELL product: redirects to CART
 * - Sticky ATC visibility logic (mobile)
 * - FAQ accordion a11y + auto-open first item + "scroll to bundle" button + GA4 event
 */

(function () {
  'use strict';

  // ==========================================================================
  // Configuration
  // ==========================================================================

  const CONFIG = {
    selectors: {
      atcButton: '[data-diabea-atc]',
      stickyBar: '.diabea-sticky-atc',
      faqItem: '.diabea-faq-item',
      toast: '.diabea-toast',
      // Hero primary CTA can be either scroll-link or ATC. We use a shared selector
      // so sticky-bar visibility logic remains stable.
      heroCTA: '.diabea-hero [data-diabea-hero-cta]',
      heroOverviewToggle: '[data-diabea-toggle-overview]',
      heroOverviewPanel: '[data-diabea-hero-overview]',
      bundleCTA: '#bundle [data-diabea-atc]',
      finalCTA: '.diabea-final-cta [data-diabea-atc]',
      smoothScrollLink: '[data-smooth-scroll]',
      scrollButton: 'button[data-scroll-to]',

      // Upsell config source (hidden section with upsell button)
      upsellSection: '#upsell',
      upsellButton: '#upsell [data-diabea-atc][data-product="upsell"][data-variant-id]'
    },
    classes: {
      visible: 'is-visible',
      loading: 'is-loading',
      modalOpen: 'is-open'
    },
    breakpoints: {
      mobile: 768
    },
    toastDuration: 4500,

    // Upsell modal behavior
    upsell: {
      enabled: true,
      // If true, modal is triggered only when main product was added
      // from one of these placements.
      triggerPlacements: ['bundle', 'sticky', 'final', 'hero', 'faq']
    },

    // Default redirect after a successful ATC when no modal is shown.
    // Supported: 'cart' | 'checkout' | 'none'
    postAtcRedirect: 'checkout'
  };

  const DEBUG = false;
  const I18N = window.diabeaI18n || {};

  function t(path, fallback = '') {
    const keys = (path || '').split('.');
    let current = I18N;
    for (const key of keys) {
      if (!current || typeof current !== 'object' || !(key in current)) return fallback;
      current = current[key];
    }
    return typeof current === 'string' ? current : fallback;
  }

  // ==========================================================================
  // Utilities
  // ==========================================================================

  function log(...args) {
    if (!DEBUG) return;
    // eslint-disable-next-line no-console
    console.log('[Diabea]', ...args);
  }

  /**
   * Safe URL joiner (avoids `//` issues).
   */
  function joinUrl(base, rel) {
    const safeBase = (base || '/').toString().replace(/\/+$/, '');
    const safeRel = (rel || '').toString().replace(/^\/+/, '');
    return `${safeBase}/${safeRel}`;
  }

  function getRootUrl() {
    return window.Shopify?.routes?.root || '/';
  }

  function getCartUrl() {
    // Prefer Liquid-provided route if available (important on multi-locale shops)
    if (window.Shopify?.routes?.cart) return window.Shopify.routes.cart;
    return joinUrl(getRootUrl(), 'cart');
  }

  function getCartAddUrl() {
    // Use cart URL as the base to avoid locale edge cases
    return joinUrl(getCartUrl(), 'add.js');
  }

  function getCheckoutUrl() {
    // Prefer Liquid-provided route if available
    if (window.Shopify?.routes?.checkout) return window.Shopify.routes.checkout;
    return '/checkout';
  }

  function debounce(fn, wait) {
    let t;
    return function (...args) {
      window.clearTimeout(t);
      t = window.setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function dispatchAnalyticsEvent(eventName, data = {}) {
    try {
      document.dispatchEvent(
        new CustomEvent(eventName, {
          detail: data,
          bubbles: true
        })
      );
      log('Analytics event:', eventName, data);
    } catch (e) {
      // Ignore if CustomEvent unsupported
    }
  }

  /**
   * Minimal GA4 helper:
   * - Uses gtag if present
   * - Falls back to pushing into dataLayer
   */
  function safeGA4(eventName, params = {}) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
        return true;
      }
    } catch (e) {
      // ignore
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...params });
      return false;
    } catch (e2) {
      return false;
    }
  }

  function toBool(val, fallback = false) {
    if (typeof val === 'boolean') return val;
    if (typeof val !== 'string') return fallback;
    const v = val.trim().toLowerCase();
    if (v === 'true' || v === '1' || v === 'yes') return true;
    if (v === 'false' || v === '0' || v === 'no') return false;
    return fallback;
  }

  // ==========================================================================
  // Upsell Modal
  // ==========================================================================

  class UpsellModal {
    constructor() {
      this.modalEl = null;
      this.primaryBtn = null;
      this.skipBtn = null;
      this.closeBtn = null;
      this.backdropBtn = null;
      this.titleEl = null;
      this.leadEl = null;
      this.listEl = null;
      this.microEl = null;

      this._originalPrimaryLabel = null;

      this.config = this.readConfig();
      if (this.isAvailable()) {
        this.createModal();
      }
    }

    readConfig() {
      const section = document.querySelector(CONFIG.selectors.upsellSection);
      const btn = document.querySelector(CONFIG.selectors.upsellButton);

      const variantId = parseInt(btn?.dataset?.variantId || '', 10);

      // Extract copy from the existing Upsell section so content stays editable
      // from Theme Editor (and we don't have to hardcode strings in JS).
      const title =
        section?.querySelector('.diabea-upgrade__title')?.textContent?.trim() ||
        t('upsell.title_fallback', 'Want more variety from day one?');

      const lead =
        section?.querySelector('.diabea-upgrade__sub')?.textContent?.trim() ||
        t('upsell.lead_fallback', 'Add Premium Pack for extra options and flexibility.');

      const priceLabel =
        section?.querySelector('.diabea-upgrade__price-now')?.textContent?.trim() ||
        '+9,90 €';

      const features = Array.from(
        section?.querySelectorAll('.diabea-upgrade__item') || []
      )
        .map((el) => (el.textContent || '').trim())
        .filter(Boolean)
        .slice(0, 6);

      return {
        enabled: CONFIG.upsell.enabled,
        variantId: Number.isFinite(variantId) && variantId > 0 ? variantId : null,
        title,
        lead,
        priceLabel,
        features,
        primaryText: t('upsell.primary', 'Add Premium Pack'),
        skipText: t('upsell.skip', 'Skip'),
        micro: t('upsell.micro', 'Optional add-on • Fast delivery • Limited stock')
      };
    }

    isAvailable() {
      return !!(
        this.config &&
        this.config.enabled &&
        typeof this.config.variantId === 'number' &&
        this.config.variantId > 0
      );
    }

    shouldTriggerForPlacement(placement) {
      if (!this.isAvailable()) return false;
      if (!Array.isArray(CONFIG.upsell.triggerPlacements) || !CONFIG.upsell.triggerPlacements.length)
        return true;
      return CONFIG.upsell.triggerPlacements.includes(placement);
    }

    createModal() {
      if (this.modalEl) return;

      const modal = document.createElement('div');
      modal.className = 'diabea-upsell-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-hidden', 'true');

      modal.innerHTML = `
        <div class="diabea-upsell-modal__backdrop" data-diabea-upsell-dismiss></div>
        <div class="diabea-upsell-modal__card" role="document">
          <div class="diabea-upsell-modal__top">
            <div class="diabea-upsell-modal__title" id="diabea-upsell-modal-title"></div>
            <button class="diabea-upsell-modal__close" type="button" aria-label="Close" data-diabea-upsell-dismiss>✕</button>
          </div>

          <div class="diabea-upsell-modal__body">
            <div class="diabea-upsell-modal__lead"></div>
            <ul class="diabea-upsell-modal__list"></ul>

            <div class="diabea-upsell-modal__actions">
              <button class="diabea-upsell-modal__btn diabea-upsell-modal__btn--primary" type="button" data-diabea-upsell-accept></button>
              <button class="diabea-upsell-modal__btn diabea-upsell-modal__btn--ghost" type="button" data-diabea-upsell-skip></button>
            </div>

            <div class="diabea-upsell-modal__micro"></div>
          </div>
        </div>
      `;

      document.body.appendChild(modal);

      this.modalEl = modal;
      this.primaryBtn = modal.querySelector('[data-diabea-upsell-accept]');
      this.skipBtn = modal.querySelector('[data-diabea-upsell-skip]');
      this.closeBtn = modal.querySelector('.diabea-upsell-modal__close');
      this.backdropBtn = modal.querySelector('.diabea-upsell-modal__backdrop');
      this.titleEl = modal.querySelector('.diabea-upsell-modal__title');
      this.leadEl = modal.querySelector('.diabea-upsell-modal__lead');
      this.listEl = modal.querySelector('.diabea-upsell-modal__list');
      this.microEl = modal.querySelector('.diabea-upsell-modal__micro');

      // Populate content
      this.titleEl.textContent = this.config.title;
      this.leadEl.innerHTML = `${this.escapeHtml(this.config.lead)} <strong>${this.escapeHtml(
        this.config.priceLabel
      )}</strong>`;

      this.listEl.innerHTML = '';
      if (this.config.features && this.config.features.length) {
        this.config.features.forEach((t) => {
          const li = document.createElement('li');
          li.textContent = `✔ ${t}`;
          this.listEl.appendChild(li);
        });
      }

      this.primaryBtn.textContent = this.config.primaryText;
      this.skipBtn.textContent = this.config.skipText;
      this.microEl.textContent = this.config.micro;

      this._originalPrimaryLabel = this.primaryBtn.textContent;
    }

    escapeHtml(str) {
      // We only use this for text coming from DOM textContent; keep it safe anyway
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    open() {
      if (!this.modalEl) return Promise.resolve('skip');

      this.modalEl.classList.add(CONFIG.classes.modalOpen);
      this.modalEl.setAttribute('aria-hidden', 'false');

      // Prevent background scroll while modal is open
      const prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';

      const restore = () => {
        document.documentElement.style.overflow = prevOverflow;
      };

      // Reset UI state
      if (this.primaryBtn) {
        this.primaryBtn.disabled = false;
        if (this._originalPrimaryLabel) this.primaryBtn.textContent = this._originalPrimaryLabel;
      }

      // eslint-disable-next-line no-unused-vars
      return new Promise((resolve) => {
        let resolved = false;

        const done = (result) => {
          if (resolved) return;
          resolved = true;

          this.close();
          restore();

          resolve(result);
        };

        const onKeyDown = (e) => {
          if (e.key === 'Escape') {
            done('skip');
          }
        };

        const onDismissClick = () => {
          done('skip');
        };

        const onAccept = () => {
          if (this.primaryBtn) {
            this.primaryBtn.disabled = true;
            this.primaryBtn.textContent = t('upsell.adding', 'Adding...');
          }
          done('accept');
        };

        const onSkip = () => {
          done('skip');
        };

        // Attach once
        window.addEventListener('keydown', onKeyDown, { once: true });
        this.backdropBtn?.addEventListener('click', onDismissClick, { once: true });
        this.closeBtn?.addEventListener('click', onDismissClick, { once: true });
        this.skipBtn?.addEventListener('click', onSkip, { once: true });
        this.primaryBtn?.addEventListener('click', onAccept, { once: true });

        // Focus primary action
        window.setTimeout(() => {
          try {
            this.primaryBtn?.focus({ preventScroll: true });
          } catch (e) {
            // ignore
          }
        }, 50);
      });
    }

    close() {
      if (!this.modalEl) return;
      this.modalEl.classList.remove(CONFIG.classes.modalOpen);
      this.modalEl.setAttribute('aria-hidden', 'true');
    }
  }

  // ==========================================================================
  // Add to Cart Handler
  // ==========================================================================

  class AddToCart {
    constructor(upsellModal) {
      this.upsellModal = upsellModal;
      this.buttons = Array.from(document.querySelectorAll(CONFIG.selectors.atcButton));
      this.toast = document.querySelector(CONFIG.selectors.toast);
      if (!this.buttons.length) return;
      this.init();
    }

    init() {
      this.buttons.forEach((btn) => {
        btn.addEventListener('click', (e) => this.handleClick(e));
      });
    }

    async handleClick(e) {
      e.preventDefault();
      const button = e.currentTarget;

      const variantId = parseInt(button.dataset.variantId || '', 10);
      const quantity = parseInt(button.dataset.quantity || '1', 10) || 1;
      const placement = button.dataset.placement || 'unknown';
      const product = button.dataset.product || 'main';
      const redirectToCart = toBool(button.dataset.redirectToCart, false);

      if (!variantId) {
        // eslint-disable-next-line no-console
        console.error('[Diabea] Missing variant ID on ATC button');
        return;
      }

      dispatchAnalyticsEvent('diabea_cta_click', { placement, product, variantId, quantity });

      this.setLoadingState(button, true);

      try {
        // Upsell = swap (remove main from cart, then add upsell)
        const response = (product === 'upsell')
          ? await this.swapMainForUpsell(variantId)
          : await this.addToCart(variantId, quantity);

        if (!response.ok) {
          let errorData = {};
          try {
            errorData = await response.json();
          } catch (e2) {
            // ignore
          }
          this.handleError(button, errorData);
          return;
        }

        // Success payload (not strictly required)
        try {
          await response.json();
        } catch (e3) {
          // ignore
        }

        dispatchAnalyticsEvent('diabea_add_to_cart', { placement, product, variantId, quantity });

        // Minimal GA4 event (optional). Does not replace proper ecommerce tracking.
        safeGA4('diabea_add_to_cart', {
          placement,
          product,
          variant_id: variantId,
          quantity,
          page_path: window.location.pathname
        });

        // Persist "main added" flag (optional)
        if (product === 'main') {
          try {
            sessionStorage.setItem('diabea_main_added', '1');
          } catch (e4) {
            // ignore
          }
        }

        // Optional toast
        this.showToast();

        // MAIN flow: offer upsell modal (if configured)
        if (product === 'main' && this.upsellModal?.isAvailable() && this.upsellModal.shouldTriggerForPlacement(placement)) {
          const decision = await this.upsellModal.open();

          dispatchAnalyticsEvent('diabea_upsell_modal_decision', {
            decision,
            placement
          });

          safeGA4('diabea_upsell_modal_decision', {
            decision,
            placement,
            page_path: window.location.pathname
          });

          if (decision === 'accept') {
            try {
              // Swap: remove main from cart, add upsell instead
              const upsellRes = await this.swapMainForUpsell(this.upsellModal.config.variantId);

              if (upsellRes && upsellRes.ok) {
                try {
                  await upsellRes.json();
                } catch (eJson) {
                  // ignore
                }

                dispatchAnalyticsEvent('diabea_add_to_cart', {
                  placement: 'upsell_modal',
                  product: 'upsell',
                  variantId: this.upsellModal.config.variantId,
                  quantity: 1
                });

                safeGA4('diabea_add_to_cart', {
                  placement: 'upsell_modal',
                  product: 'upsell',
                  variant_id: this.upsellModal.config.variantId,
                  quantity: 1,
                  page_path: window.location.pathname
                });
              }
            } catch (eUpsell) {
              // Ignore and proceed to cart
            }
          }

          const redirectUrl = CONFIG.postAtcRedirect === 'checkout' ? getCheckoutUrl() : getCartUrl();
          window.location.assign(redirectUrl);
          return;
        }

        // UPSELL or default flow
        if (redirectToCart || CONFIG.postAtcRedirect === 'cart') {
          window.location.assign(getCartUrl());
          return;
        }

        // Legacy (not used by default)
        if (CONFIG.postAtcRedirect === 'checkout') {
          window.location.assign(getCheckoutUrl());
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Diabea] Add to cart error:', error);
        this.handleError(button, { message: t('errors.generic', 'Something went wrong. Please try again.') });
      } finally {
        // If redirect happens, this won't be visible; still safe
        this.setLoadingState(button, false);
      }
    }

    addToCart(variantId, quantity) {
      return fetch(getCartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: quantity
            }
          ]
        })
      });
    }

    /**
     * Swap main product for upsell in cart.
     * 1. Fetch current cart
     * 2. Remove any main-product line items
     * 3. Add upsell variant
     */
    async swapMainForUpsell(upsellVariantId) {
      // Collect main-product variant IDs from the hidden JSON
      const mainVariantIds = new Set();
      const mainScript = document.querySelector('script[data-diabea-variants="main"]');
      if (mainScript) {
        try {
          const data = JSON.parse(mainScript.textContent);
          if (Array.isArray(data)) data.forEach(v => mainVariantIds.add(v.id));
        } catch (e) { /* skip */ }
      }

      // Fetch current cart
      let cart = { items: [] };
      try {
        const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
        cart = await cartRes.json();
      } catch (e) { /* proceed with empty cart */ }

      // Build updates: set main-product items to 0
      const updates = {};
      (cart.items || []).forEach(item => {
        if (mainVariantIds.has(item.variant_id) || mainVariantIds.has(item.id)) {
          updates[String(item.variant_id || item.id)] = 0;
        }
      });

      // Remove main items if any found
      if (Object.keys(updates).length > 0) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates })
        });
      }

      // Add upsell variant
      return this.addToCart(upsellVariantId, 1);
    }

    setLoadingState(button, isLoading) {
      const labelEl =
        button.querySelector('[data-diabea-btn-text]') ||
        button.querySelector('span') ||
        button;

      if (isLoading) {
        if (!button.dataset.originalDisabled) {
          button.dataset.originalDisabled = button.disabled ? 'true' : 'false';
        }
        if (!button.dataset.originalLabel) {
          button.dataset.originalLabel = (labelEl.textContent || '').trim();
        }

        button.classList.add(CONFIG.classes.loading);
        button.disabled = true;
        labelEl.textContent = t('ui.adding', 'Adding...');
        return;
      }

      button.classList.remove(CONFIG.classes.loading);
      const wasDisabled = button.dataset.originalDisabled === 'true';
      button.disabled = wasDisabled;

      if (button.dataset.originalLabel) {
        labelEl.textContent = button.dataset.originalLabel;
      }
    }

    handleError(button, errorData) {
      const errorMessage =
        errorData.message ||
        errorData.description ||
        t('errors.unavailable', 'This product is currently unavailable. Please try again.');

      let errorEl = button.parentElement?.querySelector?.('.diabea-atc-error');
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.className = 'diabea-atc-error';
        errorEl.style.cssText = 'color:#ef4444;font-size:0.875rem;margin-top:0.5rem;font-weight:700;';
        button.parentElement?.appendChild?.(errorEl);
      }

      errorEl.textContent = errorMessage;

      window.setTimeout(() => {
        try {
          errorEl.remove();
        } catch (e) {
          // ignore
        }
      }, 5000);
    }

    showToast() {
      if (!this.toast) return;

      this.toast.classList.add(CONFIG.classes.visible);
      window.setTimeout(() => {
        this.toast.classList.remove(CONFIG.classes.visible);
      }, CONFIG.toastDuration);
    }
  }

  // ==========================================================================
  // Sticky ATC Bar (Mobile)
  // ==========================================================================

  class StickyATC {
    constructor() {
      this.stickyBar = document.querySelector(CONFIG.selectors.stickyBar);
      this.heroCTA = document.querySelector(CONFIG.selectors.heroCTA);
      this.bundleCTA = document.querySelector(CONFIG.selectors.bundleCTA);
      this.finalCTA = document.querySelector(CONFIG.selectors.finalCTA);

      if (!this.stickyBar) return;

      this.init();
    }

    init() {
      this.setupObserver();
      window.addEventListener('resize', debounce(() => this.handleResize(), 120));
      window.addEventListener('scroll', debounce(() => this.updateVisibility(), 60), { passive: true });
    }

    setupObserver() {
      if (!('IntersectionObserver' in window)) {
        this.updateVisibility();
        return;
      }

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0
      };

      this.observer = new IntersectionObserver(() => {
        this.updateVisibility();
      }, options);

      [this.heroCTA, this.bundleCTA, this.finalCTA].forEach((el) => {
        if (el) this.observer.observe(el);
      });
    }

    updateVisibility() {
      const bundleVisible = this.isElementVisible(this.bundleCTA);
      const finalVisible = this.isElementVisible(this.finalCTA);
      const heroScrolledPast = this.isScrolledPast(this.heroCTA);

      // Show sticky when hero CTA is scrolled past, but main CTAs are not visible
      const shouldShow = heroScrolledPast && !bundleVisible && !finalVisible;
      this.stickyBar.classList.toggle(CONFIG.classes.visible, shouldShow);
    }

    isElementVisible(el) {
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    }

    isScrolledPast(el) {
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      return rect.bottom < 0;
    }

    handleResize() {
      this.updateVisibility();
    }
  }

  // ==========================================================================
  // FAQ Accordion (Details/Summary)
  // - Auto-open first item
  // - Adds "scroll to bundle" CTA inside answers
  // - Sends GA4 event on open
  // ==========================================================================

  class FAQAccordion {
    constructor() {
      this.items = Array.from(document.querySelectorAll(CONFIG.selectors.faqItem));
      if (!this.items.length) return;
      this.init();
    }

    init() {
      // Auto-open first item (fear removal)
      try {
        const anyOpen = this.items.some((d) => d.open);
        if (!anyOpen) this.items[0].open = true;
      } catch (e) {
        // ignore
      }

      this.items.forEach((item, index) => {
        const summary = item.querySelector('summary');
        const content = item.querySelector('.diabea-faq-answer');

        if (!summary || !content) return;

        const contentId = `diabea-faq-content-${index}`;
        content.id = contentId;

        summary.setAttribute('aria-controls', contentId);
        summary.setAttribute('aria-expanded', item.open ? 'true' : 'false');

        // Inject a "scroll to bundle" button into each answer (once)
        if (!content.querySelector('[data-diabea-scroll-bundle]')) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'diabea-faq-bundle-btn';
          btn.setAttribute('data-diabea-scroll-bundle', 'true');
          btn.textContent = t('ui.view_starter_pack', 'Order Diabetic Pack');

          btn.addEventListener('click', () => {
            const bundle = document.querySelector('#bundle');
            if (!bundle) return;
            bundle.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });

          content.appendChild(btn);
        }

        item.addEventListener('toggle', () => {
          summary.setAttribute('aria-expanded', item.open ? 'true' : 'false');

          if (item.open) {
            // Close other items (accordion behavior)
            this.items.forEach((other) => {
              if (other !== item && other.open) other.open = false;
            });

            const qText =
              item.querySelector('.diabea-faq-question')?.textContent?.trim() ||
              item.getAttribute('data-question') ||
              String(item.dataset.questionIndex || index);

            dispatchAnalyticsEvent('diabea_faq_open', {
              questionIndex: item.dataset.questionIndex || index,
              question: qText
            });

            safeGA4('faq_open', {
              faq_question: qText,
              page_path: window.location.pathname,
              section: 'diabea_faq'
            });
          }
        });
      });
    }
  }


  // ==========================================================================
  // Quantity Selector (Bundle) — 1× / 2× / 3×
  // ==========================================================================

  class QuantitySelector {
    constructor() {
      this.container = document.querySelector('[data-diabea-qty-bundle]');
      if (!this.container) return;

      this.options = Array.from(this.container.querySelectorAll('[data-diabea-qty-option]'));
      if (!this.options.length) return;

      this.storageKey = 'diabea_qty';

      const initialQty = this.getInitialQty();
      this.applyQty(initialQty);
      this.updateUI(initialQty);

      this.options.forEach((btn) => {
        btn.addEventListener('click', () => {
          const q = parseInt(btn.dataset.qty || '1', 10);
          if (!q || q < 1) return;

          this.saveQty(q);
          this.applyQty(q);
          this.updateUI(q);

          dispatchAnalyticsEvent('diabea_qty_select', { quantity: q });
          safeGA4('qty_select', {
            quantity: q,
            page_path: window.location.pathname,
            section: 'diabea_bundle'
          });
        });
      });
    }

    getInitialQty() {
      try {
        const saved = parseInt(sessionStorage.getItem(this.storageKey) || '1', 10);
        if (saved && saved >= 1 && saved <= 10) return saved;
      } catch (e) {
        // ignore
      }
      return 1;
    }

    saveQty(q) {
      try {
        sessionStorage.setItem(this.storageKey, String(q));
      } catch (e) {
        // ignore
      }
    }

    applyQty(q) {
      const buttons = document.querySelectorAll('[data-diabea-atc][data-product="main"]');
      buttons.forEach((btn) => {
        btn.dataset.quantity = String(q);
        btn.setAttribute('data-quantity', String(q));
      });
    }

    updateUI(q) {
      this.options.forEach((btn) => {
        const btnQty = parseInt(btn.dataset.qty || '0', 10);
        const active = btnQty === q;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-checked', active ? 'true' : 'false');
      });
    }
  }

  // ==========================================================================
  // Smooth Scroll
  // ==========================================================================

  class SmoothScroll {
    constructor() {
      this.links = Array.from(document.querySelectorAll(CONFIG.selectors.smoothScrollLink));
      this.buttons = Array.from(document.querySelectorAll(CONFIG.selectors.scrollButton));

      if (!this.links.length && !this.buttons.length) return;
      this.init();
    }

    init() {
      this.links.forEach((link) => link.addEventListener('click', (e) => this.handleClick(e)));
      this.buttons.forEach((btn) => btn.addEventListener('click', (e) => this.handleClick(e)));
    }

    handleClick(e) {
      const el = e.currentTarget;
      let targetId;

      if (el.tagName === 'A') {
        targetId = el.getAttribute('href');
      } else {
        targetId = el.dataset.scrollTo;
      }

      if (!targetId || !targetId.startsWith('#') || targetId === '#') return;

      const target = document.querySelector(targetId);
      if (!target) return;

      e.preventDefault();

      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });

      if (history.pushState) {
        history.pushState(null, '', targetId);
      }
    }
  }

  class HeroOverviewToggle {
    constructor() {
      this.buttons = Array.from(document.querySelectorAll(CONFIG.selectors.heroOverviewToggle));
      if (!this.buttons.length) return;
      this.init();
    }

    init() {
      this.buttons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.toggle(button);
        });
      });
    }

    getPanel(button) {
      const panelId = button.getAttribute('aria-controls');
      return panelId ? document.getElementById(panelId) : null;
    }

    getSource(button) {
      const selector = button.dataset.overviewSource;
      return selector ? document.querySelector(selector) : null;
    }

    sanitizeClonedMarkup(source) {
      const clone = source.cloneNode(true);
      clone.querySelectorAll('*').forEach((node) => {
        Array.from(node.attributes).forEach((attr) => {
          if (attr.name.indexOf('data-shopify') === 0) {
            node.removeAttribute(attr.name);
          }
        });
      });
      return clone.innerHTML;
    }

    populate(panel, source) {
      if (panel.dataset.loaded === 'true') return;
      panel.innerHTML = this.sanitizeClonedMarkup(source);
      panel.dataset.loaded = 'true';
    }

    toggle(button) {
      const panel = this.getPanel(button);
      const source = this.getSource(button);
      if (!panel || !source) return;

      const willOpen = panel.hidden;
      if (willOpen) {
        this.populate(panel, source);
        panel.hidden = false;
      } else {
        panel.hidden = true;
      }

      button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    }
  }

  // ==========================================================================
  // Animate on Scroll
  // ==========================================================================

  class AnimateOnScroll {
    constructor() {
      this.elements = Array.from(document.querySelectorAll('[data-animate]'));
      if (!this.elements.length) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.init();
    }

    init() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: just show elements
        this.elements.forEach((el) => {
          el.style.opacity = '1';
          el.style.transform = 'none';
        });
        return;
      }

      const options = {
        root: null,
        rootMargin: '0px 0px -50px 0px',
        threshold: 0.15
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('diabea-animate-in');
          this.observer.unobserve(entry.target);
        });
      }, options);

      this.elements.forEach((el) => {
        el.style.opacity = '0';
        el.style.transform = 'translateY(30px)';
        this.observer.observe(el);
      });
    }
  }

  // ==========================================================================
  // Stat Counter
  // ==========================================================================

  class StatCounter {
    constructor() {
      this.stats = Array.from(document.querySelectorAll('[data-stat-value]'));
      if (!this.stats.length) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.init();
    }

    init() {
      if (!('IntersectionObserver' in window)) return;

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.5
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.animateCounter(entry.target);
          this.observer.unobserve(entry.target);
        });
      }, options);

      this.stats.forEach((stat) => this.observer.observe(stat));
    }

    animateCounter(element) {
      const targetValue = (element.dataset.statValue || '').toString();
      const prefix = element.dataset.statPrefix || '';
      const suffix = element.dataset.statSuffix || '';

      const numericValue = parseInt(targetValue.replace(/\D/g, ''), 10);
      if (Number.isNaN(numericValue)) return;

      const duration = 2000;
      const steps = 60;
      const increment = numericValue / steps;
      let current = 0;

      const timer = window.setInterval(() => {
        current += increment;

        if (current >= numericValue) {
          element.textContent = `${prefix}${this.formatNumber(numericValue)}${suffix}`;
          window.clearInterval(timer);
          return;
        }

        element.textContent = `${prefix}${this.formatNumber(Math.floor(current))}${suffix}`;
      }, duration / steps);
    }

    formatNumber(num) {
      try {
        const locale =
          document.documentElement.lang ||
          window.Shopify?.locale ||
          navigator.language ||
          'en-US';
        return num.toLocaleString(locale);
      } catch (e) {
        return String(num);
      }
    }
  }

  // ==========================================================================
  // Initialize
  // ==========================================================================

  function initModules() {
    new QuantitySelector();

    const upsellModal = new UpsellModal();

    new AddToCart(upsellModal);
    new StickyATC();
      new FAQAccordion();
      new SmoothScroll();
      new HeroOverviewToggle();
      new AnimateOnScroll();
    new StatCounter();

    log('Landing initialized');

    try {
      document.dispatchEvent(new CustomEvent('diabea:ready'));
    } catch (e) {
      // ignore
    }
  }

  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initModules);
    } else {
      initModules();
    }
  }

  init();
})();

// ==========================================================================
// CRO v4 Modules
// ==========================================================================

(function () {
  'use strict';

  // ======== FOMO TICKER ROTATION ========
  class FomoTicker {
    constructor() {
      this.el = document.querySelector('[data-diabea-fomo-ticker]');
      if (!this.el) return;
      this.msgs = Array.from(this.el.querySelectorAll('.diabea-fomo-ticker-msg'));
      if (this.msgs.length < 2) return;
      this.current = 0;
      this.interval = null;
      this.start();
    }

    start() {
      this.interval = window.setInterval(() => this.next(), 5000);
    }

    next() {
      this.msgs[this.current].classList.remove('is-active');
      this.current = (this.current + 1) % this.msgs.length;
      this.msgs[this.current].classList.add('is-active');
    }
  }

  // ======== COUNTDOWN TIMER ========
  class CountdownTimer {
    constructor() {
      this.timers = Array.from(document.querySelectorAll('[data-diabea-countdown]'));
      this.miniTimers = Array.from(document.querySelectorAll('[data-diabea-countdown-mini]'));
      if (!this.timers.length && !this.miniTimers.length) return;

      // Get or create session-based deadline
      this.deadline = this.getDeadline();
      this.tick();
      this.intervalId = window.setInterval(() => this.tick(), 1000);
    }

    getDeadline() {
      const key = 'diabea_countdown_deadline';
      let stored = null;
      try { stored = sessionStorage.getItem(key); } catch (e) { /* ignore */ }

      if (stored) {
        const d = new Date(stored);
        if (d > new Date()) return d;
      }

      // Use hours from first timer element, default 4
      const hours = parseInt(
        (this.timers[0] && this.timers[0].dataset.hours) || '4', 10
      ) || 4;
      const deadline = new Date(Date.now() + hours * 3600 * 1000);
      try { sessionStorage.setItem(key, deadline.toISOString()); } catch (e) { /* ignore */ }
      return deadline;
    }

    tick() {
      const diff = Math.max(0, this.deadline - Date.now());
      if (diff <= 0) {
        this.timers.forEach(t => { t.textContent = '00:00:00'; });
        this.miniTimers.forEach(t => { t.textContent = '00:00'; });
        window.clearInterval(this.intervalId);
        return;
      }

      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      const pad = n => String(n).padStart(2, '0');

      const full = `${pad(h)}:${pad(m)}:${pad(s)}`;
      const mini = `${pad(h)}h ${pad(m)}m`;

      this.timers.forEach(t => { t.textContent = full; });
      this.miniTimers.forEach(t => { t.textContent = mini; });
    }
  }

  // ======== LIVE VIEWER COUNT ========
  class ViewerCounter {
    constructor() {
      this.el = document.querySelector('[data-diabea-viewers-count]');
      if (!this.el) return;
      this.base = 8 + Math.floor(Math.random() * 10); // 8-17
      this.el.textContent = String(this.base);
      this.intervalId = window.setInterval(() => this.fluctuate(), 8000 + Math.random() * 12000);
    }

    fluctuate() {
      const delta = Math.random() > 0.5 ? 1 : -1;
      this.base = Math.max(5, Math.min(28, this.base + delta));
      this.el.textContent = String(this.base);
    }
  }

  // ======== FOMO TOAST NOTIFICATIONS ========
  class FomoToast {
    constructor() {
      this.el = document.querySelector('[data-diabea-fomo-toast]');
      this.textEl = document.querySelector('[data-diabea-fomo-text]');
      if (!this.el || !this.textEl) return;

      // Localized data
      this.names = ['Marija', 'Ivan', 'Ana', 'Tomislav', 'Vesna', 'Damir', 'Petra', 'Josip', 'Maja', 'Luka', 'Ivana', 'Matej'];
      this.cities = ['Zagreb', 'Split', 'Rijeka', 'Osijek', 'Pula', 'Zadar', 'Slavonski Brod', 'Varaždin', 'Dubrovnik', 'Šibenik'];
      this.product = t('ui.pack_name', 'Diabetic Pack');

      // First toast after 30-60 sec, then every 30-90 sec
      this.showTimeout = null;
      this.scheduleNext(30000 + Math.random() * 30000);
    }

    scheduleNext(delay) {
      this.showTimeout = window.setTimeout(() => this.show(), delay);
    }

    show() {
      const name = this.names[Math.floor(Math.random() * this.names.length)];
      const city = this.cities[Math.floor(Math.random() * this.cities.length)];
      const mins = Math.floor(2 + Math.random() * 45);

      this.textEl.textContent = `${name} iz ${city} — ${this.product} (prije ${mins} min)`;
      this.el.classList.add('is-visible');
      this.el.setAttribute('aria-hidden', 'false');

      window.setTimeout(() => {
        this.el.classList.remove('is-visible');
        this.el.setAttribute('aria-hidden', 'true');
        this.scheduleNext(30000 + Math.random() * 60000);
      }, 5000);
    }
  }

  // ======== EXIT INTENT POPUP ========
  class ExitIntent {
    constructor() {
      this.el = document.querySelector('[data-diabea-exit-intent]');
      if (!this.el) return;

      this.shown = false;
      this.timerId = null;
      this.timerRemaining = 0;

      // Respectful gating (set via section settings)
      this.delaySeconds = parseInt(this.el.dataset.delaySeconds || '60', 10);
      this.minScrollPercent = parseInt(this.el.dataset.minScroll || '40', 10);
      this.requireBundleSeen = toBool(this.el.dataset.requireBundle, false);

      this.delayPassed = this.delaySeconds <= 0;
      if (!this.delayPassed) {
        window.setTimeout(() => { this.delayPassed = true; }, this.delaySeconds * 1000);
      }

      this.maxScrollPercent = 0;
      this.bundleSeen = false;

      // Check sessionStorage: show only once per session
      try {
        if (sessionStorage.getItem('diabea_exit_shown')) return;
      } catch (e) { /* ignore */ }

      // Track bundle seen (optional)
      this.initBundleSeenObserver();

      // Track scroll depth (max %)
      this.onScroll = () => this.trackScrollDepth();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.trackScrollDepth();

      // Desktop: mouse leaves viewport top
      document.addEventListener('mouseout', (e) => {
        if (e.clientY <= 0 && !this.shown) this.attemptOpen('mouseout_top');
      });

      // Mobile: scroll up pattern (only after sufficient depth)
      let lastScrollY = window.scrollY;
      let scrollUpCount = 0;
      window.addEventListener('scroll', () => {
        if (this.shown) return;
        const currentY = window.scrollY;
        if (currentY < lastScrollY && lastScrollY - currentY > 120) {
          scrollUpCount++;
          if (scrollUpCount >= 3 && currentY < 220) this.attemptOpen('scroll_up');
        } else if (currentY > lastScrollY) {
          scrollUpCount = 0;
        }
        lastScrollY = currentY;
      }, { passive: true });

      // Bind dismiss
      this.el.querySelectorAll('[data-diabea-exit-dismiss]').forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // ESC key
      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.el.classList.contains('is-open')) this.close();
      });
    }

    trackScrollDepth() {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop || 0;
      const scrollHeight = doc.scrollHeight || 1;
      const viewport = window.innerHeight || 1;
      const maxScrollable = Math.max(1, scrollHeight - viewport);
      const pct = Math.min(100, Math.max(0, (scrollTop / maxScrollable) * 100));
      this.maxScrollPercent = Math.max(this.maxScrollPercent, pct);

      // Fallback bundle detection if IntersectionObserver is not available
      if (this.requireBundleSeen && !this.bundleSeen && !('IntersectionObserver' in window)) {
        const bundleEl = document.querySelector('#bundle');
        if (bundleEl) {
          const rect = bundleEl.getBoundingClientRect();
          if (rect.top < window.innerHeight * 0.65) {
            this.setBundleSeen();
          }
        }
      }
    }

    initBundleSeenObserver() {
      if (!this.requireBundleSeen) return;

      // Read cached flag
      try {
        if (sessionStorage.getItem('diabea_bundle_seen') === '1') {
          this.bundleSeen = true;
          return;
        }
      } catch (e) { /* ignore */ }

      const bundleEl = document.querySelector('#bundle');
      if (!bundleEl) return;

      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
            this.setBundleSeen();
            observer.disconnect();
          }
        });
      }, { threshold: [0.0, 0.2, 0.6] });

      observer.observe(bundleEl);
    }

    setBundleSeen() {
      this.bundleSeen = true;
      try { sessionStorage.setItem('diabea_bundle_seen', '1'); } catch (e) { /* ignore */ }
    }

    isEligible() {
      if (this.shown) return false;
      if (!this.delayPassed) return false;

      if (this.minScrollPercent > 0 && this.maxScrollPercent < this.minScrollPercent) return false;

      if (this.requireBundleSeen && !this.bundleSeen) return false;

      // Don't show if user already added main product
      try {
        if (sessionStorage.getItem('diabea_main_added')) return false;
      } catch (e) { /* ignore */ }

      return true;
    }

    attemptOpen(trigger) {
      if (!this.isEligible()) return;

      dispatchAnalyticsEvent('diabea_exit_intent_trigger', {
        trigger,
        max_scroll_pct: Math.round(this.maxScrollPercent),
        delay_seconds: this.delaySeconds,
        require_bundle_seen: this.requireBundleSeen
      });

      this.open();
    }

    open() {
      if (this.shown) return;
      this.shown = true;

      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');

      try { sessionStorage.setItem('diabea_exit_shown', '1'); } catch (e) { /* ignore */ }

      // Start countdown only when popup opens
      this.startTimer();
    }

    close() {
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');

      if (this.timerId) {
        window.clearTimeout(this.timerId);
        this.timerId = null;
      }
    }

    startTimer() {
      const timerEl = this.el.querySelector('[data-diabea-exit-countdown]');
      const wrapper = this.el.querySelector('[data-diabea-exit-timer]');
      if (!timerEl || !wrapper) return;

      const minutes = parseInt(wrapper.dataset.minutes || this.el.dataset.minutes || '15', 10);
      this.timerRemaining = Math.max(0, minutes * 60);

      const tick = () => {
        if (!this.el.classList.contains('is-open')) return;
        if (this.timerRemaining <= 0) {
          timerEl.textContent = '0:00';
          return;
        }

        const m = Math.floor(this.timerRemaining / 60);
        const s = this.timerRemaining % 60;
        timerEl.textContent = `${m}:${String(s).padStart(2, '0')}`;
        this.timerRemaining--;

        this.timerId = window.setTimeout(tick, 1000);
      };

      tick();
    }
  }

  // ======== INIT CRO MODULES ========
  function initCRO() {
    new FomoTicker();
    // new CountdownTimer(); // Disabled by default (avoid fake urgency)
    // ViewerCounter disabled — fake numbers violate EU Directive 2005/29/EC
    // FomoToast disabled — fake purchase notifications violate EU unfair practices directive
    new ExitIntent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCRO);
  } else {
    initCRO();
  }
})();

// ==========================================================================
// Size Drawer v10.2 — bottom sheet, works from ANY ATC button
// ==========================================================================
(function () {
  'use strict';

  var STORAGE_KEY = 'diabea_sock_size';
  var selectedSizeLabel = null;
  var variantMaps = {};
  var pendingATCButton = null;
  var drawer, optionsContainer, confirmBtn;
  var prevRootOverflow = '';
  var prevBodyOverflow = '';

  // Persist / restore size from sessionStorage so it survives page reload
  function saveSize(label) {
    selectedSizeLabel = label;
    try { sessionStorage.setItem(STORAGE_KEY, label); } catch (e) { /* ignore */ }
  }
  function clearSavedSize() {
    selectedSizeLabel = null;
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
  }
  function restoreSize() {
    try { selectedSizeLabel = sessionStorage.getItem(STORAGE_KEY) || null; } catch (e) { /* ignore */ }
  }

  function loadVariantMaps() {
    var els = document.querySelectorAll('script[data-diabea-variants]');
    for (var i = 0; i < els.length; i++) {
      var scope = els[i].getAttribute('data-diabea-variants');
      try {
        var data = JSON.parse(els[i].textContent);
        if (Array.isArray(data) && data.length > 0) variantMaps[scope] = data;
      } catch (e) { /* skip */ }
    }
  }

  function findVariantId(scope, label) {
    var map = variantMaps[scope];
    if (!map) return null;
    for (var i = 0; i < map.length; i++) {
      if (map[i].title === label) return map[i].id;
    }
    return null;
  }

  function applyVariantToButtons(scope, variantId) {
    var pt = scope === 'upsell' ? 'upsell' : 'main';
    var btns = document.querySelectorAll('[data-diabea-atc][data-product="' + pt + '"]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].dataset.variantId = variantId;
      btns[i].setAttribute('data-variant-id', String(variantId));
    }
  }

  function hasVariants(scope) {
    return variantMaps[scope] && variantMaps[scope].length > 1;
  }

  function getScope(atcBtn) {
    return atcBtn.dataset.product === 'upsell' ? 'upsell' : 'main';
  }

  // ---- Drawer open ----
  function openDrawer(scope) {
    if (!drawer) return;
    var variants = variantMaps[scope];
    if (!variants) return;
    var selectedVariantId = selectedSizeLabel ? findVariantId(scope, selectedSizeLabel) : null;
    if (selectedSizeLabel && !selectedVariantId) {
      clearSavedSize();
    }

    optionsContainer.innerHTML = '';
    for (var i = 0; i < variants.length; i++) {
      (function (v) {
        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'diabea-size-drawer__pill';
        if (selectedVariantId && v.title === selectedSizeLabel) pill.classList.add('is-active');
        pill.setAttribute('role', 'radio');
        pill.setAttribute('aria-checked', selectedVariantId && selectedSizeLabel === v.title ? 'true' : 'false');
        pill.textContent = v.title;

        pill.addEventListener('click', function () {
          var pills = optionsContainer.querySelectorAll('.diabea-size-drawer__pill');
          for (var j = 0; j < pills.length; j++) {
            pills[j].classList.remove('is-active');
            pills[j].setAttribute('aria-checked', 'false');
          }
          pill.classList.add('is-active');
          pill.setAttribute('aria-checked', 'true');
          saveSize(v.title);
          confirmBtn.disabled = false;
        });

        optionsContainer.appendChild(pill);
      })(variants[i]);
    }

    confirmBtn.disabled = !selectedVariantId;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    prevRootOverflow = document.documentElement.style.overflow;
    prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  // ---- Drawer close ----
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = prevRootOverflow;
    document.body.style.overflow = prevBodyOverflow;
  }

  // ---- Confirm: apply size to ALL scopes, then re-trigger ATC ----
  function confirmSelection() {
    if (!selectedSizeLabel || !pendingATCButton) return;

    var scopes = Object.keys(variantMaps);
    for (var i = 0; i < scopes.length; i++) {
      var vid = findVariantId(scopes[i], selectedSizeLabel);
      if (vid) applyVariantToButtons(scopes[i], vid);
    }

    closeDrawer();

    var btn = pendingATCButton;
    pendingATCButton = null;
    setTimeout(function () { btn.click(); }, 80);
  }

  // ---- ATC intercept (capture phase) ----
  function interceptATC(e) {
    var atcBtn = e.target.closest('[data-diabea-atc]');
    if (!atcBtn) return;

    var scope = getScope(atcBtn);
    if (!hasVariants(scope)) return;

    if (selectedSizeLabel) {
      var vid = findVariantId(scope, selectedSizeLabel);
      if (vid) {
        applyVariantToButtons(scope, vid);
        return;
      }
      clearSavedSize();
    }

    e.preventDefault();
    e.stopImmediatePropagation();
    pendingATCButton = atcBtn;
    openDrawer(scope);
  }

  function init() {
    loadVariantMaps();
    if (Object.keys(variantMaps).length === 0) return;

    drawer = document.querySelector('[data-diabea-size-drawer]');
    if (!drawer) return;

    optionsContainer = drawer.querySelector('[data-diabea-size-drawer-options]');
    confirmBtn = drawer.querySelector('[data-diabea-size-drawer-confirm]');

    // Restore saved size from previous page visit
    restoreSize();
    if (selectedSizeLabel) {
      var scopes = Object.keys(variantMaps);
      var hasValidStoredSize = false;
      for (var s = 0; s < scopes.length; s++) {
        var vid = findVariantId(scopes[s], selectedSizeLabel);
        if (vid) {
          applyVariantToButtons(scopes[s], vid);
          hasValidStoredSize = true;
        }
      }
      if (!hasValidStoredSize) {
        clearSavedSize();
      }
    }

    var closers = drawer.querySelectorAll('[data-diabea-size-drawer-close]');
    for (var i = 0; i < closers.length; i++) {
      closers[i].addEventListener('click', function () { closeDrawer(); pendingATCButton = null; });
    }

    confirmBtn.addEventListener('click', confirmSelection);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
        pendingATCButton = null;
      }
    });

    document.addEventListener('click', interceptATC, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
